import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { SalePointClient } from '../../helpers/client';
import { ImageLoader } from '../imgLoader/imgLoader';
import StoreIcon from '../../logo/store.png';
import { NearestSalesPoint, SalesPointType } from 'orderme-api-integration-client';
import { useEffect, useState, useCallback, useMemo } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { clearDelivery, deliverToSalesPoint } from '../../redux/reducers/pickupReducer';
import { useAppDispatch } from '../../redux/hooks';
import { useGeolocation } from '../../hooks/useGeolocation';

interface SalesPointItemProps {
  onClick: () => void;
  isSelected: boolean;
  itemMustBeSelected?: boolean;
}

export function SalesPointItem(props: SalesPointItemProps) {
  const { onClick, isSelected, itemMustBeSelected = false } = props;
  const { t } = useTranslation();
  const salesPointClient = useMemo(() => new SalePointClient(), []);
  const [salesPoints, setSalesPoints] = useState<NearestSalesPoint[]>();
  const [filteredSalesPoints, setFilteredSalesPoints] = useState<NearestSalesPoint[]>([]);
  const [selectedSalesPoint, setSelectedSalesPoint] = useState<NearestSalesPoint>(null);
  const [anySalesPoint, setAnySalesPoint] = useState<boolean>(false);
  const [cities, setCities] = useState<string[]>();
  const [selectedCity, setSelectedCity] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const getPoints = useCallback(() => {
    salesPointClient
      .getByType(SalesPointType.SalesPoint)
      .then((response) => {
        var points = response.map(
          (point) =>
            new NearestSalesPoint({
              value: point,
              distanceInDegrees: undefined,
              distanceInMeters: undefined,
            })
        );

        setSalesPoints(points);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [salesPointClient]);

  const getPointsByLocation = useCallback(
    (longitude: number, latitude: number) => {
      salesPointClient
        .getByLocation(longitude, latitude)
        .then((response) => {
          const filteredByDistance = response
            .filter((point) => point.value.type === SalesPointType.SalesPoint)
            .sort((a, b) => a.distanceInMeters - b.distanceInMeters);
          setSalesPoints(filteredByDistance);

          var responseCities = response.map((res) => res.value.city);
          var uniqueCities = responseCities.filter((c, index) => {
            return responseCities.indexOf(c) === index;
          });
          setCities(uniqueCities);
          if (filteredByDistance.length > 0) {
            setSelectedCity(filteredByDistance[0].value.city);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [salesPointClient]
  );

  const { requestLocation } = useGeolocation({
    onLocation: getPointsByLocation,
    onPermissionDenied: getPoints,
  });

  useEffect(() => {
    if (!isSelected) {
      setSelectedSalesPoint(null);
      setAnySalesPoint(false);
      return;
    }

    if (!salesPoints) {
      setIsLoading(true);
      requestLocation();
    }
  }, [isSelected, salesPoints, requestLocation]);

  useEffect(() => {
    if (salesPoints) {
      var cities = salesPoints.map((res) => res.value.city);
      var uniqueCities = cities.filter((c, index) => {
        return cities.indexOf(c) === index;
      });
      // If no distance, then sort by name
      if (salesPoints.length > 0 && !salesPoints[0].distanceInMeters) {
        uniqueCities = uniqueCities.sort((a, b) => a.localeCompare(b, 'lt'));
      }
      setCities(uniqueCities);
    }
  }, [salesPoints]);

  useEffect(() => {
    if (anySalesPoint) {
      setSelectedCity(null);
    }
  }, [anySalesPoint]);

  useEffect(() => {
    if (selectedCity) {
      var citySalesPoints = salesPoints.filter((salesPoint) => salesPoint.value.city === selectedCity);
      // If no distance, then sort by name
      if (citySalesPoints.length > 0 && !citySalesPoints[0].distanceInDegrees) {
        citySalesPoints = citySalesPoints.sort((a, b) => a.value.name.localeCompare(b.value.name, 'lt'));
      }

      setFilteredSalesPoints(citySalesPoints);
      setAnySalesPoint(false);
    } else {
      setFilteredSalesPoints([]);
    }
  }, [salesPoints, selectedCity]);

  useEffect(() => {
    if (filteredSalesPoints?.length === 1) {
      setSelectedSalesPoint(filteredSalesPoints[0]);
    } else {
      setSelectedSalesPoint(null);
    }
  }, [filteredSalesPoints]);

  useEffect(() => {
    if (selectedSalesPoint) {
      dispatch(deliverToSalesPoint({ salesPointId: selectedSalesPoint.value.id }));
    } else if (anySalesPoint) {
      dispatch(deliverToSalesPoint(null));
    } else {
      dispatch(clearDelivery());
    }
  }, [selectedSalesPoint, anySalesPoint, dispatch]);

  const filterOptions = createFilterOptions({
    stringify: (option: NearestSalesPoint) => option.value.name + option.value.address,
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: '20px' }}>
      <Accordion expanded={isSelected} sx={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={isSelected ? <RadioButtonCheckedIcon color="secondary" /> : <RadioButtonUncheckedIcon />}
          aria-controls="panel-content"
          id="panel-header"
          sx={{ flexDirection: 'row-reverse' }}
          onClick={onClick}
          style={{ cursor: 'pointer' }}
        >
          <Box display="flex" alignItems="center" sx={{ width: '65px', ml: '10px' }}>
            <ImageLoader src={StoreIcon} sx={{ height: '50px' }} alt="SalesPoint" />
          </Box>
          <Typography sx={{ alignContent: 'center' }}>{t('salesPoint:salesPoints_one')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container sx={{ px: '20px' }}>
            <Grid
              container
              direction={{ xs: 'column', md: 'row' }}
              size={{ xs: 12 }}
              spacing={2}
              sx={{ justifyContent: 'center' }}
            >
              <Grid size="auto">
                <Autocomplete
                  disablePortal
                  value={selectedCity ?? null}
                  options={cities ?? []}
                  disabled={isLoading || anySalesPoint}
                  loading={isLoading}
                  getOptionLabel={(option) => option}
                  onChange={(_, option) => setSelectedCity(option)}
                  sx={{
                    width: { xs: '100%', md: '300px' },
                    '& fieldset': { borderRadius: '15px' },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('salesPoint:selectCity')}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size="grow">
                <Autocomplete
                  filterOptions={filterOptions}
                  sx={{
                    width: '100%',
                    '& fieldset': { borderRadius: '15px' },
                  }}
                  slotProps={{
                    listbox: {
                      style: { maxHeight: '300px' },
                    },
                  }}
                  value={selectedSalesPoint ?? null}
                  blurOnSelect
                  options={filteredSalesPoints ?? []}
                  disabled={isLoading || !selectedCity || anySalesPoint}
                  onChange={(_, option) => setSelectedSalesPoint(option)}
                  getOptionLabel={(option) => option.value.name}
                  renderInput={(params) => <TextField {...params} label={t('salesPoint:selectSalesPoint')} />}
                  renderOption={(props, parcelLocker) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={key} component="li" {...optionProps}>
                        <Grid
                          container
                          sx={{
                            justifyContent: 'space-between',
                            display: 'grid',
                            gridTemplateColumns: 'auto auto',
                            width: '100%',
                          }}
                        >
                          <Grid container sx={{ alignItems: 'center' }}>
                            <Typography sx={{ pr: '10px' }}>{parcelLocker.value.name}</Typography>
                            <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                              {parcelLocker.value.address}
                            </Typography>
                          </Grid>
                          <Grid
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '30px',
                            }}
                          >
                            {parcelLocker.distanceInMeters && (
                              <Typography sx={{ color: '#1f4a8f' }}>
                                {(parcelLocker.distanceInMeters / 1000).toFixed(2)}
                                km
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }}
                />
              </Grid>
            </Grid>
            {!itemMustBeSelected && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  mt: '15px',
                  mr: '10px',
                  cursor: 'pointer',
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
                onClick={() => setAnySalesPoint(!anySalesPoint)}
              >
                <Grid size="auto">
                  {anySalesPoint ? (
                    <CheckBoxIcon color="secondary" />
                  ) : (
                    <CheckBoxOutlineBlankIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                  )}
                </Grid>
                <Grid size="grow">
                  <Typography sx={{ ml: '10px' }}>{t('salesPoint:anySalesPoint')}</Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
